import { v4 } from "uuid";
import { getAddOnRates } from "@simplyk/common";
import { getInitialGroupMemberCounts } from "./getInitialGroupMemberCounts";

import { getPreviewPurchasedNumber } from "@/common/helpers/rates";
import { TicketingPaymentInput } from "@/common/types/ticketing";
import { QuestionOutput, RateOutput, RateByOccurrenceOutput } from "@/common/types/trpc";

interface PrefillAvailability {
  seats?: number | null;
  rates: Pick<RateByOccurrenceOutput, "rateId" | "remainingTickets" | "soldTicketsCount">[];
}

const getRemainingMemberCapacity = (rateId: string, rates: RateOutput[], availability: PrefillAvailability): number => {
  const currentRate = availability.rates.find((rate) => rate.rateId === rateId);
  if (!currentRate) {
    return 0;
  }
  const addOnRateIds = new Set(getAddOnRates(rates).map((rate) => rate.id));
  const totalSold = availability.rates.reduce(
    (total, rate) => total + (addOnRateIds.has(rate.rateId) ? 0 : (rate.soldTicketsCount ?? 0)),
    0
  );
  return Math.max(0, Math.min(currentRate.remainingTickets ?? Infinity, (availability.seats || Infinity) - totalSold));
};

export const initTicketsPurchased = (
  rates: RateOutput[],
  isPreview: boolean,
  prefillRateId?: string,
  remainingMemberCapacity?: number
) => {
  return rates.map((rate, index) => {
    const isPrefilled = Boolean(prefillRateId) && rate.id === prefillRateId;
    const isPreselectedInPreview = isPreview && index === 0;
    const groupCount = getPreviewPurchasedNumber(rate);
    const hasCapacity =
      !rate.groupMembershipMaxMembers || (remainingMemberCapacity ?? rate.remainingTickets ?? Infinity) >= groupCount;
    return {
      rateId: rate.id,
      purchasedNumber: (isPreselectedInPreview || isPrefilled) && hasCapacity ? groupCount : 0,
      attendeesByTicket: rate.attendeesByTicket || 1,
    };
  });
};

export const getPrefilledRateSelection = ({
  rates,
  questions,
  prefillRateId,
  isPreview,
  isMemberSomeoneElse = false,
  availability,
}: {
  rates: RateOutput[];
  questions: QuestionOutput[];
  prefillRateId: string | undefined;
  isPreview: boolean;
  isMemberSomeoneElse?: boolean;
  availability?: PrefillAvailability;
}): Pick<TicketingPaymentInput, "ticketsPurchased" | "customAnswersOfParticipants"> => {
  const selectedRate = prefillRateId ? rates.find((rate) => rate.id === prefillRateId) : undefined;
  const remainingMemberCapacity =
    selectedRate?.groupMembershipMaxMembers && availability
      ? getRemainingMemberCapacity(selectedRate.id, rates, availability)
      : undefined;
  const ticketsPurchased = initTicketsPurchased(rates, isPreview, prefillRateId, remainingMemberCapacity);
  const customAnswersOfParticipants = selectedRate
    ? buildParticipantsForRate(selectedRate, questions, isMemberSomeoneElse, remainingMemberCapacity)
    : [];
  return { ticketsPurchased, customAnswersOfParticipants };
};

const buildParticipantsForRate = (
  rate: RateOutput,
  questions: QuestionOutput[],
  isMemberSomeoneElse: boolean,
  remainingMemberCapacity = rate.remainingTickets ?? Infinity
) => {
  const quantity = rate.minimumToBuy || 1;
  const memberCounts = rate.groupMembershipMaxMembers
    ? getInitialGroupMemberCounts(quantity, remainingMemberCapacity)
    : Array.from({ length: quantity }, () => rate.attendeesByTicket || 1);
  const rateQuestions = questions.filter(
    (question) => question.hasRate && question.rateQuestions?.some((rateQuestion) => rateQuestion.rateId === rate.id)
  );

  return memberCounts.flatMap((memberCount, groupIndex) =>
    Array.from({ length: memberCount }, (_unused, index) => ({
      rateId: rate.id,
      ...(rate.groupMembershipMaxMembers
        ? {
            membershipGroupIndex: groupIndex,
            isForBuyer: groupIndex === 0 && index === 0 && !isMemberSomeoneElse,
          }
        : {}),
      answers: rateQuestions.map((question) => generateDefaultQuestionAnswer(question.id)),
      ...(isMemberSomeoneElse && groupIndex === 0 && index === 0 ? { isForBuyer: false } : {}),
    }))
  );
};

const generateDefaultQuestionAnswer = (questionId: string) => {
  return {
    id: v4(),
    questionId,
    choiceIds: [],
    answer: "",
    answers: [""],
  };
};
